import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { allRemoteSettingsSelect } from "../redux/selectors/index";
import CampaignIcon from "@mui/icons-material/Campaign";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

// ─── localStorage helpers ────────────────────────────────────────────────────
const LS_KEY = "readBroadcasts";

const getReadIds = () => {
    try {
        return JSON.parse(localStorage.getItem(LS_KEY) || "[]");
    } catch {
        return [];
    }
};

const markAllRead = (news) => {
    try {
        const allIds = news.map((n) => String(n.id));
        localStorage.setItem(LS_KEY, JSON.stringify(allIds));
    } catch { }
};

// ─── Exported helper to count unread (used by Section.js for badge) ──────────
export const getUnreadCount = (news = []) => {
    const readIds = getReadIds();
    return news.filter((n) => !readIds.includes(String(n.id))).length;
};

// ─── Play iOS-style notification sound using Web Audio API ───────────────────
const playNotificationSound = () => {
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const ctx = new AudioContext();
        const now = ctx.currentTime;

        const playPop = (freq, time, vol) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();

            osc.type = "sine";
            osc.frequency.setValueAtTime(freq - 100, time);
            osc.frequency.exponentialRampToValueAtTime(freq, time + 0.03);

            gain.gain.setValueAtTime(0, time);
            gain.gain.linearRampToValueAtTime(vol, time + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.001, time + 0.12);

            osc.connect(gain);
            gain.connect(ctx.destination);

            osc.start(time);
            osc.stop(time + 0.15);
        };

        playPop(987.77, now, 0.15);
        playPop(1318.51, now + 0.08, 0.2);
    } catch (e) {
        console.warn("Autoplay block or audio context error:", e);
    }
};

// ─── Main Component ──────────────────────────────────────────────────────────
export default function BroadcastModal({ open, onClose, forceOpen = false }) {
    const site_settings = useSelector(allRemoteSettingsSelect);
    const news = site_settings?.news || [];

    const [visible, setVisible] = useState(false);
    const [animating, setAnimating] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [imageError, setImageError] = useState({});
    const [scanLine, setScanLine] = useState(0);
    const [glitchActive, setGlitchActive] = useState(false);

    const broadcastsToShow = news.slice().reverse();
    const total = broadcastsToShow.length;

    // Scan line animation
    useEffect(() => {
        if (!visible) return;
        let frame;
        let start = null;
        const animate = (ts) => {
            if (!start) start = ts;
            const elapsed = (ts - start) % 3000;
            setScanLine(Math.floor((elapsed / 3000) * 100));
            frame = requestAnimationFrame(animate);
        };
        frame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(frame);
    }, [visible]);

    // Occasional subtle glitch
    useEffect(() => {
        if (!animating) return;
        const interval = setInterval(() => {
            setGlitchActive(true);
            setTimeout(() => setGlitchActive(false), 120);
        }, 4000 + Math.random() * 3000);
        return () => clearInterval(interval);
    }, [animating]);

    useEffect(() => {
        if (!forceOpen && total > 0) {
            const unreadCount = getUnreadCount(news);
            if (unreadCount > 0) {
                triggerOpen();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [total]);

    useEffect(() => {
        if (open) {
            setCurrentIndex(0);
            triggerOpen();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const triggerOpen = () => {
        setVisible(true);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setAnimating(true);
                playNotificationSound();
            });
        });
    };

    const handleClose = useCallback(() => {
        setAnimating(false);
        setTimeout(() => {
            setVisible(false);
            markAllRead(news);
            onClose?.();
        }, 450);
    }, [news, onClose]);

    const handlePrev = () => {
        setCurrentIndex((i) => Math.max(0, i - 1));
    };

    const handleNext = () => {
        if (currentIndex < total - 1) {
            setCurrentIndex((i) => i + 1);
        } else {
            handleClose();
        }
    };

    if (!visible || total === 0) return null;

    const item = broadcastsToShow[currentIndex];
    const isLast = currentIndex === total - 1;
    const progressPct = total > 1 ? ((currentIndex + 1) / total) * 100 : 100;

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=Syne+Mono&display=swap');

                @keyframes bc-rise {
                    0%  { opacity: 0; transform: translateY(56px) scale(0.94); filter: blur(8px); }
                    60% { opacity: 1; filter: blur(0px); }
                    100%{ opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
                }
                @keyframes bc-sink {
                    0%  { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
                    100%{ opacity: 0; transform: translateY(48px) scale(0.95); filter: blur(6px); }
                }
                @keyframes bc-fade-in  { from { opacity: 0; } to { opacity: 1; } }
                @keyframes bc-fade-out { from { opacity: 1; } to { opacity: 0; } }

                @keyframes bc-scanline {
                    0%   { transform: translateY(-100%); }
                    100% { transform: translateY(1000%); }
                }
                @keyframes bc-pulse-ring {
                    0%   { transform: scale(1);    opacity: 0.6; }
                    100% { transform: scale(1.9);  opacity: 0; }
                }
                @keyframes bc-shimmer {
                    0%   { background-position: -400px 0; }
                    100% { background-position: 400px 0; }
                }
                @keyframes bc-progress {
                    from { width: 0%; }
                    to   { width: ${progressPct}%; }
                }
                @keyframes bc-glitch-x {
                    0%,100% { clip-path: inset(0 0 100% 0); transform: translateX(0); }
                    10%     { clip-path: inset(20% 0 60% 0); transform: translateX(-4px); }
                    20%     { clip-path: inset(50% 0 30% 0); transform: translateX(4px); }
                    30%     { clip-path: inset(10% 0 80% 0); transform: translateX(-2px); }
                    100%    { clip-path: inset(0 0 100% 0); }
                }
                @keyframes bc-icon-glow {
                    0%,100%{ box-shadow: 0 0 0 0 rgba(14,165,233,0.0), inset 0 1px 0 rgba(255,255,255,0.6); }
                    50%    { box-shadow: 0 0 18px 4px rgba(14,165,233,0.15), inset 0 1px 0 rgba(255,255,255,0.8); }
                }
                @keyframes bc-border-trace {
                    0%  { opacity: 0.3; }
                    50% { opacity: 0.8; }
                    100%{ opacity: 0.3; }
                }
                @keyframes bc-dot-breathe {
                    0%,100% { transform: scale(1); opacity: 0.5; }
                    50%     { transform: scale(1.3); opacity: 1; }
                }
                @keyframes bc-badge-in {
                    0%  { opacity: 0; transform: translateY(-6px); }
                    100%{ opacity: 1; transform: translateY(0); }
                }

                .bc-backdrop-enter { animation: bc-fade-in  0.35s ease forwards; }
                .bc-backdrop-exit  { animation: bc-fade-out 0.4s  ease forwards; }
                .bc-card-enter     { animation: bc-rise 0.5s cubic-bezier(0.22,1,0.36,1) forwards; }
                .bc-card-exit      { animation: bc-sink 0.4s cubic-bezier(0.4,0,0.6,1)  forwards; }

                .bc-card {
                    position: relative;
                    width: 100%;
                    max-width: 420px;
                    max-height: 76vh;
                    margin: 0 16px;
                    background: rgba(255, 255, 255, 0.85);
                    border-radius: 28px;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    border: 1px solid rgba(14, 165, 233, 0.18);
                    backdrop-filter: blur(20px);
                    -webkit-backdrop-filter: blur(20px);
                    box-shadow:
                        0 0 0 1px rgba(255,255,255,0.75) inset,
                        0 32px 64px -16px rgba(15,23,42,0.12),
                        0 0 60px -15px rgba(14,165,233,0.1);
                }

                .bc-glitch-overlay {
                    position: absolute;
                    inset: 0;
                    pointer-events: none;
                    z-index: 20;
                    background: rgba(14, 165, 233, 0.03);
                    animation: bc-glitch-x 0.12s steps(1) forwards;
                }

                .bc-border-animate {
                    animation: bc-border-trace 2.5s ease-in-out infinite;
                }

                .bc-scanline-track {
                    position: absolute;
                    left: 0; right: 0;
                    height: 2px;
                    background: linear-gradient(90deg, transparent, rgba(14, 165, 233, 0.3), transparent);
                    pointer-events: none;
                    z-index: 15;
                    transition: top 0.05s linear;
                }

                .bc-grid-bg {
                    position: absolute;
                    inset: 0;
                    background-image:
                        linear-gradient(rgba(14, 165, 233, 0.03) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(14, 165, 233, 0.03) 1px, transparent 1px);
                    background-size: 28px 28px;
                    pointer-events: none;
                }

                .bc-noise {
                    position: absolute;
                    inset: 0;
                    pointer-events: none;
                    opacity: 0.015;
                    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
                    background-size: 200px 200px;
                }

                .bc-close-btn {
                    position: absolute;
                    top: 14px; right: 14px;
                    z-index: 30;
                    width: 30px; height: 30px;
                    border-radius: 50%;
                    border: 1px solid rgba(15, 23, 42, 0.08);
                    background: rgba(15, 23, 42, 0.04);
                    backdrop-filter: blur(12px);
                    display: flex; align-items: center; justify-content: center;
                    cursor: pointer; outline: none;
                    transition: all 0.2s ease;
                    color: rgba(71, 85, 105, 0.8);
                }
                .bc-close-btn:hover {
                    background: rgba(239, 68, 68, 0.08);
                    border-color: rgba(239, 68, 68, 0.2);
                    color: #ef4444;
                    transform: scale(1.08);
                }

                .bc-nav-btn {
                    width: 44px; height: 44px;
                    border-radius: 14px;
                    border: 1px solid rgba(14, 165, 233, 0.2);
                    background: rgba(14, 165, 233, 0.05);
                    display: flex; align-items: center; justify-content: center;
                    cursor: pointer; outline: none;
                    transition: all 0.2s ease;
                    color: #0284c7;
                    flex-shrink: 0;
                }
                .bc-nav-btn:hover {
                    background: rgba(14, 165, 233, 0.1);
                    border-color: rgba(14, 165, 233, 0.35);
                    transform: scale(1.05);
                }
                .bc-nav-btn:active { transform: scale(0.95); }

                .bc-cta {
                    flex: 1;
                    height: 44px;
                    border-radius: 14px;
                    border: none;
                    font-size: 13px;
                    font-weight: 600;
                    font-family: 'Space Grotesk', sans-serif;
                    cursor: pointer; outline: none;
                    display: flex; align-items: center; justify-content: center;
                    gap: 6px;
                    letter-spacing: 0.03em;
                    position: relative;
                    overflow: hidden;
                    transition: transform 0.15s ease, box-shadow 0.2s ease;
                }
                .bc-cta::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 50%, transparent 100%);
                    background-size: 400px 100%;
                    animation: bc-shimmer 2.4s linear infinite;
                }
                .bc-cta-next {
                    background: linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%);
                    color: #fff;
                    box-shadow: 0 6px 20px rgba(14,165,233,0.25), 0 0 0 1px rgba(56,189,248,0.2) inset;
                }
                .bc-cta-next:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 10px 24px rgba(14,165,233,0.35), 0 0 0 1px rgba(56,189,248,0.3) inset;
                }
                .bc-cta-done {
                    background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
                    color: #fff;
                    box-shadow: 0 6px 20px rgba(16,185,129,0.25), 0 0 0 1px rgba(52,211,153,0.2) inset;
                }
                .bc-cta-done:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 10px 24px rgba(16,185,129,0.35), 0 0 0 1px rgba(52,211,153,0.3) inset;
                }
                .bc-cta:active { transform: translateY(0) scale(0.98); }

                .bc-content {
                    flex: 1;
                    overflow-y: auto;
                    padding: 12px 22px 4px;
                    font-size: 14px;
                    line-height: 1.7;
                    color: rgba(51, 65, 85, 0.95);
                    font-family: 'Space Grotesk', sans-serif;
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                .bc-content::-webkit-scrollbar { display: none; }
                .bc-content img { max-width: 100%; border-radius: 10px; margin: 8px 0; }
                .bc-content a { color: #0284c7; text-decoration: none; border-bottom: 1px solid rgba(14, 165, 233, 0.4); }
                .bc-content p { margin: 0 0 8px; }
                .bc-content strong { font-weight: 600; color: #0f172a; }

                .bc-dot-active {
                    background: #0284c7;
                    box-shadow: 0 0 8px rgba(14, 165, 233, 0.4);
                }
                .bc-dot-inactive {
                    background: rgba(14, 165, 233, 0.18);
                    animation: bc-dot-breathe 2.4s ease-in-out infinite;
                }

                .bc-icon-ring {
                    animation: bc-pulse-ring 2s ease-out infinite;
                }
                .bc-icon-box {
                    animation: bc-icon-glow 3s ease-in-out infinite;
                }

                .bc-badge {
                    animation: bc-badge-in 0.4s 0.2s ease both;
                }

                .bc-progress-bar {
                    animation: bc-progress 0.6s cubic-bezier(0.22,1,0.36,1) forwards;
                }

                .bc-label-text {
                    font-family: 'Syne Mono', monospace;
                    font-size: 9px;
                    letter-spacing: 0.18em;
                    text-transform: uppercase;
                    color: #0284c7;
                }

                /* ── Theme-Responsive Modal Backdrops and Elements ── */
                .bc-backdrop {
                    position: fixed;
                    inset: 0;
                    z-index: 9998;
                    background: rgba(241, 245, 249, 0.65);
                    backdrop-filter: blur(24px) saturate(1.8);
                    -webkit-backdrop-filter: blur(24px) saturate(1.8);
                    display: flex;
                    align-items: flex-end;
                    justify-content: center;
                    padding: 0 0 72px 0;
                    transition: background 0.3s ease;
                }
                
                [data-theme="dark"] .bc-backdrop {
                    background: rgba(8, 11, 17, 0.75);
                }

                .bc-image-gradient {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(to bottom, transparent 30%, rgba(255, 255, 255, 0.95) 100%);
                }
                
                [data-theme="dark"] .bc-image-gradient {
                    background: linear-gradient(to bottom, transparent 30%, rgba(15, 23, 42, 0.95) 100%);
                }

                [data-theme="dark"] .bc-card {
                    background: rgba(15, 23, 42, 0.85); /* Deep glassmorphic dark slate */
                    border-color: rgba(14, 165, 233, 0.3);
                    box-shadow:
                        0 0 0 1px rgba(255, 255, 255, 0.05) inset,
                        0 32px 64px -16px rgba(0, 0, 0, 0.5),
                        0 0 60px -15px rgba(14, 165, 233, 0.15);
                }

                [data-theme="dark"] .bc-content,
                [data-theme="dark"] .bc-content * {
                    background: transparent !important;
                    background-color: transparent !important;
                    color: rgba(226, 232, 240, 0.95) !important; /* Soft slate-200 */
                }

                [data-theme="dark"] .bc-content strong {
                    color: #ffffff !important;
                }

                [data-theme="dark"] .bc-content a {
                    color: #38bdf8 !important; /* Premium sky-400 */
                    border-bottom-color: rgba(56, 189, 248, 0.4) !important;
                }

                [data-theme="dark"] .bc-close-btn {
                    border-color: rgba(255, 255, 255, 0.1);
                    background: rgba(255, 255, 255, 0.05);
                    color: rgba(226, 232, 240, 0.8);
                }
                [data-theme="dark"] .bc-close-btn:hover {
                    background: rgba(239, 68, 68, 0.15);
                    border-color: rgba(239, 68, 68, 0.3);
                    color: #f87171;
                }

                [data-theme="dark"] .bc-grid-bg {
                    background-image:
                        linear-gradient(rgba(14, 165, 233, 0.05) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(14, 165, 233, 0.05) 1px, transparent 1px);
                }
            `}</style>

            {/* ── Backdrop ── */}
            <div
                className={`${animating ? "bc-backdrop-enter" : "bc-backdrop-exit"} bc-backdrop`}
                onClick={handleClose}
            >
                {/* ── Card ── */}
                <div
                    className={`bc-card ${animating ? "bc-card-enter" : "bc-card-exit"}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Grid background */}
                    <div className="bc-grid-bg" />
                    {/* Noise texture */}
                    <div className="bc-noise" />
                    {/* Glitch overlay (conditional) */}
                    {glitchActive && <div className="bc-glitch-overlay" />}
                    {/* Scan line */}
                    <div
                        className="bc-scanline-track"
                        style={{ top: `${scanLine}%` }}
                    />

                    {/* ── Top corner decorations ── */}
                    <div style={{ position: "absolute", top: 0, left: 0, width: 28, height: 28, pointerEvents: "none", zIndex: 10 }}>
                        <div style={{ position: "absolute", top: 8, left: 8, width: 10, height: 1, background: "rgba(14,165,233,0.4)" }} />
                        <div style={{ position: "absolute", top: 8, left: 8, width: 1, height: 10, background: "rgba(14,165,233,0.4)" }} />
                    </div>
                    <div style={{ position: "absolute", top: 0, right: 0, width: 28, height: 28, pointerEvents: "none", zIndex: 10 }}>
                        <div style={{ position: "absolute", top: 8, right: 8, width: 10, height: 1, background: "rgba(14,165,233,0.4)" }} />
                        <div style={{ position: "absolute", top: 8, right: 8, width: 1, height: 10, background: "rgba(14,165,233,0.4)" }} />
                    </div>
                    <div style={{ position: "absolute", bottom: 0, left: 0, width: 28, height: 28, pointerEvents: "none", zIndex: 10 }}>
                        <div style={{ position: "absolute", bottom: 8, left: 8, width: 10, height: 1, background: "rgba(14,165,233,0.4)" }} />
                        <div style={{ position: "absolute", bottom: 8, left: 8, width: 1, height: 10, background: "rgba(14,165,233,0.4)" }} />
                    </div>
                    <div style={{ position: "absolute", bottom: 0, right: 0, width: 28, height: 28, pointerEvents: "none", zIndex: 10 }}>
                        <div style={{ position: "absolute", bottom: 8, right: 8, width: 10, height: 1, background: "rgba(14,165,233,0.4)" }} />
                        <div style={{ position: "absolute", bottom: 8, right: 8, width: 1, height: 10, background: "rgba(14,165,233,0.4)" }} />
                    </div>

                    {/* ── Close button ── */}
                    <button className="bc-close-btn" onClick={handleClose}>
                        <CloseIcon style={{ fontSize: 14 }} />
                    </button>

                    {/* ── Hero image ── */}
                    {item.image && !imageError[item.id] && (
                        <div style={{ position: "relative", width: "100%", height: 175, flexShrink: 0, overflow: "hidden" }}>
                            <img
                                src={item.image}
                                alt={item.subject}
                                onError={() => setImageError((prev) => ({ ...prev, [item.id]: true }))}
                                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", filter: "brightness(0.96) saturate(1.1)" }}
                            />
                            <div className="bc-image-gradient" />
                            {/* Horizontal scan lines overlay on image */}
                            <div style={{
                                position: "absolute", inset: 0,
                                backgroundImage: "repeating-linear-gradient(0deg, rgba(0,0,0,0.03) 0px, rgba(0,0,0,0.03) 1px, transparent 1px, transparent 3px)",
                                pointerEvents: "none",
                            }} />
                        </div>
                    )}

                    {/* ── Progress bar (top of card, absolute) ── */}
                    <div style={{
                        position: "absolute",
                        top: 0, left: 0, right: 0,
                        height: 2,
                        background: "rgba(14,165,233,0.08)",
                        zIndex: 25,
                        borderRadius: "28px 28px 0 0",
                        overflow: "hidden",
                    }}>
                        <div
                            key={currentIndex}
                            className="bc-progress-bar"
                            style={{
                                height: "100%",
                                background: "linear-gradient(90deg, #0284c7, #0ea5e9, #38bdf8)",
                                boxShadow: "0 0 8px rgba(14,165,233,0.5)",
                                borderRadius: 2,
                            }}
                        />
                    </div>

                    {/* ── Header ── */}
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: item.image && !imageError[item.id] ? "14px 22px 0" : "22px 22px 0",
                        flexShrink: 0,
                        position: "relative",
                        zIndex: 5,
                    }}>
                        {/* Icon with pulse ring */}
                        <div style={{ position: "relative", flexShrink: 0 }}>
                            {/* Pulse ring */}
                            <div className="bc-icon-ring" style={{
                                position: "absolute",
                                inset: -4,
                                borderRadius: 14,
                                border: "1px solid rgba(14,165,233,0.3)",
                            }} />
                            <div className="bc-icon-box" style={{
                                width: 36, height: 36,
                                borderRadius: 12,
                                background: "linear-gradient(135deg, rgba(14,165,233,0.15) 0%, rgba(56,189,248,0.08) 100%)",
                                border: "1px solid rgba(14,165,233,0.25)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                                <CampaignIcon style={{ fontSize: 17, color: "#0284c7" }} />
                            </div>
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span className="bc-label-text">Broadcast</span>
                                {total > 1 && (
                                    <span className="bc-badge" style={{
                                        fontSize: 9,
                                        fontFamily: "'Syne Mono', monospace",
                                        color: "rgba(14,165,233,0.7)",
                                        letterSpacing: "0.1em",
                                    }}>
                                        {String(currentIndex + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Subject ── */}
                    <div style={{ padding: "10px 22px 0", flexShrink: 0, position: "relative", zIndex: 5 }}>
                        <h2 style={{
                            fontSize: 20,
                            fontWeight: 700,
                            fontFamily: "'Space Grotesk', sans-serif",
                            color: "var(--text-main, #0f172a)",
                            lineHeight: 1.2,
                            margin: 0,
                            letterSpacing: "-0.01em",
                        }}>
                            {item.subject}
                        </h2>
                        {item.timestamp && (
                            <p style={{
                                fontSize: 10,
                                color: "var(--text-muted, rgba(100,116,139,0.8))",
                                fontFamily: "'Syne Mono', monospace",
                                margin: "5px 0 0",
                                letterSpacing: "0.08em",
                            }}>
                                {item.timestamp}
                            </p>
                        )}
                    </div>

                    {/* ── Thin divider with glow ── */}
                    <div style={{
                        margin: "12px 22px 0",
                        height: 1,
                        background: "linear-gradient(90deg, transparent, rgba(14,165,233,0.15) 30%, rgba(14,165,233,0.15) 70%, transparent)",
                        flexShrink: 0,
                        position: "relative",
                        zIndex: 5,
                    }} />

                    {/* ── Message body ── */}
                    <div
                        className="bc-content"
                        style={{ position: "relative", zIndex: 5 }}
                        dangerouslySetInnerHTML={{ __html: item.message }}
                    />

                    {/* ── Pagination dots ── */}
                    {total > 1 && (
                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 6,
                            padding: "8px 22px 0",
                            flexShrink: 0,
                            position: "relative",
                            zIndex: 5,
                        }}>
                            {broadcastsToShow.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setCurrentIndex(idx)}
                                    className={idx === currentIndex ? "bc-dot-active" : "bc-dot-inactive"}
                                    style={{
                                        width: idx === currentIndex ? 22 : 5,
                                        height: 5,
                                        borderRadius: 3,
                                        border: "none",
                                        padding: 0,
                                        cursor: "pointer",
                                        outline: "none",
                                        transition: "all 0.3s cubic-bezier(0.34,1.56,0.64,1)",
                                    }}
                                />
                            ))}
                        </div>
                    )}

                    {/* ── Footer ── */}
                    <div style={{
                        display: "flex",
                        gap: 8,
                        padding: "12px 22px 22px",
                        flexShrink: 0,
                        position: "relative",
                        zIndex: 5,
                    }}>
                        {currentIndex > 0 && (
                            <button className="bc-nav-btn" onClick={handlePrev}>
                                <ChevronLeftIcon style={{ fontSize: 20 }} />
                            </button>
                        )}

                        <button
                            className={`bc-cta ${isLast ? "bc-cta-done" : "bc-cta-next"}`}
                            onClick={handleNext}
                        >
                            {isLast ? (
                                <>
                                    <CheckCircleOutlineIcon style={{ fontSize: 15 }} />
                                    Got it
                                </>
                            ) : (
                                <>
                                    Continue
                                    <ChevronRightIcon style={{ fontSize: 15 }} />
                                </>
                            )}
                        </button>
                    </div>

                    {/* ── Bottom glow ambience ── */}
                    <div style={{
                        position: "absolute",
                        bottom: -40,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 200,
                        height: 80,
                        borderRadius: "50%",
                        background: isLast
                            ? "rgba(16,185,129,0.08)"
                            : "rgba(14,165,233,0.06)",
                        filter: "blur(30px)",
                        pointerEvents: "none",
                        zIndex: 1,
                        transition: "background 0.4s ease",
                    }} />
                </div>
            </div>
        </>
    );
}